import React from 'react';
import { TableExpandButton } from '../../components/elements';
import { useTranslate } from '../../mixins/translate/translate';

export type BreakDownOpenState = {
    [key: string]: boolean;
};
export function SurveyDetailsElementGroupReportPanel(props: { children: React.ReactNode; extraColumns?: string[] }) {
    const translate = useTranslate();
    return (
        <table className="table">
            <thead className="mt10">
                <tr className="dark">
                    <th style={{ width: '50px' }}>{translate('common.code')}</th>
                    <th>{translate('common.part')}</th>
                    <th>{translate('common.material')}</th>
                    {props.extraColumns &&
                        props.extraColumns.map((columns, i) => (
                            <th key={i} className="text-right" style={{ width: '40px' }}>
                                {columns}
                            </th>
                        ))}
                    <th>&nbsp;</th>
                </tr>
            </thead>
            {props.children}
        </table>
    );
}
export function SurveyDetailsElementGroupPanel(props: {
    children: React.ReactNode;
    elementGroupName: string;
    extraColumns?: React.ReactNode[];
    breakDownOpenState: BreakDownOpenState;
    onChangeBreakdownOpenState: (key: string) => void;
    hasChildren: boolean;
}) {
    const translate = useTranslate();
    return (
        <>
            <thead key={props.elementGroupName}>
                <tr style={{ backgroundColor: '#bed2dc' }}>
                    <th></th>
                    <th>{translate(`elementGroupName.${props.elementGroupName}`)}</th>
                    <th>&nbsp;</th>
                    {props.extraColumns &&
                        props.extraColumns.map((extraColumn, i) => (
                            <th key={i} className="text-center" style={{ width: '30px' }}>
                                {extraColumn}
                            </th>
                        ))}
                    <th className="text-right">
                        {props.hasChildren && (
                            <TableExpandButton
                                open={props.breakDownOpenState[props.elementGroupName]}
                                onClick={() => props.onChangeBreakdownOpenState(props.elementGroupName)}
                            />
                        )}
                    </th>
                </tr>
            </thead>
            {props.breakDownOpenState[props.elementGroupName] && <>{props.children}</>}
        </>
    );
}
export function SurveyDetailsElementGroupPanelDetails(props: { children: React.ReactNode }) {
    return (
        <tbody className="br-n">
            <tr>
                <td></td>
                <td colSpan={4}>
                    <div className="p10 br2 br-a br-greyer" style={{ backgroundColor: '#d9e5ec' }}>
                        {props.children}
                    </div>
                </td>
            </tr>
        </tbody>
    );
}
export function SurveyDetailsElementPanel(props: {
    children: React.ReactNode;
    element: {
        id: string;
        code: string;
        name: string;
        units: Record<string, unknown>[];
    };
    extraColumns?: React.ReactNode[];
    breakDownOpenState: BreakDownOpenState;
    onChangeBreakdownOpenState: (key: string) => void;
}) {
    return (
        <>
            <thead className="object-table-element">
                <tr className="primary">
                    <th>{props.element.code}</th>
                    <th>{props.element.name}</th>
                    <th>&nbsp;</th>
                    {props.extraColumns &&
                        props.extraColumns.map((extraColumn, i) => (
                            <th key={i} className="text-center" style={{ width: '30px' }}>
                                {extraColumn}
                            </th>
                        ))}

                    <th className="text-right">
                        {props.element.units && props.element.units.length > 0 && (
                            <TableExpandButton
                                open={props.breakDownOpenState[props.element.id]}
                                onClick={() => props.onChangeBreakdownOpenState(props.element.id)}
                            />
                        )}
                    </th>
                </tr>
            </thead>
            {props.breakDownOpenState[props.element.id] && <>{props.children}</>}
        </>
    );
}

export function SurveyDetailsUnitPanelDetails(props: { children: React.ReactNode }) {
    return (
        <tbody className="br-n">
            <tr>
                <td></td>
                <td colSpan={4}>
                    <div className="p10 bg-light darker br2 br-a br-greyer">{props.children}</div>
                </td>
            </tr>
        </tbody>
    );
}

export function SurveyDetailsUnitPanel(props: {
    children: React.ReactNode;
    unit: {
        id: string;
        code: string;
        name: string;
        material: string;
        manifestations: Record<string, unknown>[];
    };
    breakDownOpenState: BreakDownOpenState;
    onChangeBreakdownOpenState: (key: string) => void;
    extraColumns?: React.ReactNode[];
}) {
    return (
        <>
            <tbody className="object-table-unit">
                <tr className="bg-light darker">
                    <td>{props.unit.code}</td>
                    <td>{props.unit.name}</td>
                    <td>{props.unit.material}</td>
                    {props.extraColumns &&
                        props.extraColumns.map((extraColumn, i) => (
                            <td key={i} className="text-center" style={{ width: '30px' }}>
                                {extraColumn}
                            </td>
                        ))}
                    <td className="text-right">
                        <TableExpandButton
                            open={props.breakDownOpenState[props.unit.id]}
                            onClick={() => props.onChangeBreakdownOpenState(props.unit.id)}
                        />
                    </td>
                </tr>
            </tbody>
            {props.breakDownOpenState[props.unit.id] && <>{props.children}</>}
        </>
    );
}

export function SurveyDetailsManifestationPanel(props: {
    children: React.ReactNode;
    manifestation: {
        id: string;
        name: string;
    };
    breakDownOpenState: BreakDownOpenState;
    onChangeBreakdownOpenState: (key: string) => void;
    extraColumns?: React.ReactNode[];
}) {
    return (
        <>
            <tbody className="object-table-manifestation">
                <tr className="bg-light">
                    <td>&nbsp;</td>
                    <td>{props.manifestation.name}</td>
                    <td>&nbsp;</td>
                    {props.extraColumns &&
                        props.extraColumns.map((extraColumn, i) => (
                            <td key={i} className="text-center" style={{ width: '30px' }}>
                                {extraColumn}
                            </td>
                        ))}
                    <td className="text-right">
                        <TableExpandButton
                            open={props.breakDownOpenState[props.manifestation.id]}
                            onClick={() => props.onChangeBreakdownOpenState(props.manifestation.id)}
                        />
                    </td>
                </tr>
            </tbody>
            {props.breakDownOpenState[props.manifestation.id] && (
                <>
                    <tbody>
                        <tr>
                            <td></td>
                            <td colSpan={4}>
                                <div className="p10 bg-light darker br2 br-a br-greyer">{props.children}</div>
                            </td>
                        </tr>
                    </tbody>
                </>
            )}
        </>
    );
}
